import React, { useState } from 'react';
import { ChevronDown, ChevronUp, Lock, BookOpen, Clock, Target } from 'lucide-react';
import { motion, AnimatePresence } from 'framer-motion';

const RoadmapTeaserSection = ({ careerPath, onUnlock }) => {
  const [expandedWeek, setExpandedWeek] = useState(0);

  // Generate career-specific roadmap preview based on career path
  const generateCareerSpecificWeeks = (career) => {
    const careerLower = (career || '').toLowerCase();
    
    // Define career-specific content
    let week1, week2;
    
    if (careerLower.includes('full stack') || careerLower.includes('fullstack')) {
      week1 = {
        week: 1,
        title: "Full Stack Foundations",
        description: "Master both frontend and backend basics",
        tasks: [
          { title: "Set up Node.js and React development environment", duration: "2 hours", type: "setup" },
          { title: "Learn HTTP, REST APIs, and client-server architecture", duration: "4 hours", type: "video" },
          { title: "Build a simple full-stack todo app", duration: "5 hours", type: "project" },
          { title: "Understand database basics (SQL vs NoSQL)", duration: "2 hours", type: "reading" }
        ],
        unlocked: true
      };
      week2 = {
        week: 2,
        title: "Backend Development & APIs",
        description: "Deep dive into server-side development",
        tasks: [
          { title: "Learn Express.js framework fundamentals", duration: "5 hours", type: "video" },
          { title: "Build RESTful API with authentication", duration: "6 hours", type: "project" },
          { title: "Database design and modeling exercises", duration: "3 hours", type: "practice" },
          { title: "Deploy your API to cloud platform", duration: "2 hours", type: "setup" }
        ],
        unlocked: true
      };
    } else if (careerLower.includes('data scientist') || careerLower.includes('data science')) {
      week1 = {
        week: 1,
        title: "Data Science Fundamentals",
        description: "Learn Python and essential data science libraries",
        tasks: [
          { title: "Set up Python, Jupyter, and Anaconda", duration: "2 hours", type: "setup" },
          { title: "Master NumPy and Pandas basics", duration: "5 hours", type: "video" },
          { title: "Data cleaning and exploration project", duration: "4 hours", type: "project" },
          { title: "Statistics and probability fundamentals", duration: "3 hours", type: "reading" }
        ],
        unlocked: true
      };
      week2 = {
        week: 2,
        title: "Machine Learning Basics",
        description: "Introduction to ML algorithms and models",
        tasks: [
          { title: "Learn Scikit-learn library", duration: "4 hours", type: "video" },
          { title: "Build classification and regression models", duration: "6 hours", type: "project" },
          { title: "Practice on Kaggle datasets", duration: "5 hours", type: "practice" },
          { title: "Model evaluation and validation techniques", duration: "2 hours", type: "reading" }
        ],
        unlocked: true
      };
    } else if (careerLower.includes('frontend') || careerLower.includes('front-end')) {
      week1 = {
        week: 1,
        title: "Modern Frontend Foundations",
        description: "Master HTML, CSS, and JavaScript fundamentals",
        tasks: [
          { title: "Set up modern frontend development tools", duration: "2 hours", type: "setup" },
          { title: "Learn ES6+ JavaScript features", duration: "4 hours", type: "video" },
          { title: "Build responsive landing page", duration: "4 hours", type: "project" },
          { title: "CSS Grid and Flexbox mastery", duration: "3 hours", type: "practice" }
        ],
        unlocked: true
      };
      week2 = {
        week: 2,
        title: "React Fundamentals",
        description: "Learn component-based UI development",
        tasks: [
          { title: "React basics: Components, Props, State", duration: "5 hours", type: "video" },
          { title: "Build interactive React application", duration: "6 hours", type: "project" },
          { title: "Practice React hooks and lifecycle", duration: "4 hours", type: "practice" },
          { title: "Learn state management patterns", duration: "2 hours", type: "reading" }
        ],
        unlocked: true
      };
    } else if (careerLower.includes('backend') || careerLower.includes('back-end')) {
      week1 = {
        week: 1,
        title: "Backend Engineering Basics",
        description: "Server-side development fundamentals",
        tasks: [
          { title: "Set up Node.js or Python backend environment", duration: "2 hours", type: "setup" },
          { title: "Learn API design principles and REST", duration: "4 hours", type: "video" },
          { title: "Build your first API server", duration: "5 hours", type: "project" },
          { title: "Database fundamentals and SQL", duration: "3 hours", type: "reading" }
        ],
        unlocked: true
      };
      week2 = {
        week: 2,
        title: "Database & Authentication",
        description: "Data persistence and security",
        tasks: [
          { title: "Database design and relationships", duration: "4 hours", type: "video" },
          { title: "Implement JWT authentication system", duration: "6 hours", type: "project" },
          { title: "Practice complex SQL queries", duration: "4 hours", type: "practice" },
          { title: "Learn about API security best practices", duration: "2 hours", type: "reading" }
        ],
        unlocked: true
      };
    } else if (careerLower.includes('product manager') || careerLower.includes('product management')) {
      week1 = {
        week: 1,
        title: "Product Management Fundamentals",
        description: "Learn product lifecycle and strategy basics",
        tasks: [
          { title: "Set up PM tools (Jira, Figma, Analytics)", duration: "2 hours", type: "setup" },
          { title: "Learn product development lifecycle", duration: "4 hours", type: "video" },
          { title: "User research and persona creation exercise", duration: "4 hours", type: "project" },
          { title: "Read: The Lean Startup methodology", duration: "3 hours", type: "reading" }
        ],
        unlocked: true
      };
      week2 = {
        week: 2,
        title: "Product Strategy & Roadmapping",
        description: "Build product vision and prioritization skills",
        tasks: [
          { title: "Learn prioritization frameworks (RICE, ICE)", duration: "4 hours", type: "video" },
          { title: "Create a product roadmap for a mock app", duration: "6 hours", type: "project" },
          { title: "Practice writing user stories and PRDs", duration: "4 hours", type: "practice" },
          { title: "Study competitive analysis techniques", duration: "2 hours", type: "reading" }
        ],
        unlocked: true
      };
    } else if (careerLower.includes('devops') || careerLower.includes('sre')) {
      week1 = {
        week: 1,
        title: "DevOps & Infrastructure Basics",
        description: "Learn cloud platforms and automation",
        tasks: [
          { title: "Set up AWS/Azure account and CLI tools", duration: "2 hours", type: "setup" },
          { title: "Learn Linux fundamentals and bash scripting", duration: "5 hours", type: "video" },
          { title: "Deploy a simple app to cloud platform", duration: "4 hours", type: "project" },
          { title: "Study CI/CD concepts and pipelines", duration: "2 hours", type: "reading" }
        ],
        unlocked: true
      };
      week2 = {
        week: 2,
        title: "Containerization & Orchestration",
        description: "Master Docker and Kubernetes basics",
        tasks: [
          { title: "Learn Docker fundamentals and best practices", duration: "5 hours", type: "video" },
          { title: "Containerize and deploy a microservices app", duration: "6 hours", type: "project" },
          { title: "Practice Kubernetes basics with Minikube", duration: "4 hours", type: "practice" },
          { title: "Set up monitoring with Prometheus", duration: "2 hours", type: "setup" }
        ],
        unlocked: true
      };
    } else if (careerLower.includes('mobile') || careerLower.includes('ios') || careerLower.includes('android')) {
      week1 = {
        week: 1,
        title: "Mobile Development Basics",
        description: "Get started with mobile app development",
        tasks: [
          { title: "Set up React Native or Flutter environment", duration: "2 hours", type: "setup" },
          { title: "Learn mobile UI/UX design principles", duration: "4 hours", type: "video" },
          { title: "Build your first mobile app (Hello World+)", duration: "5 hours", type: "project" },
          { title: "Study mobile app architecture patterns", duration: "2 hours", type: "reading" }
        ],
        unlocked: true
      };
      week2 = {
        week: 2,
        title: "Navigation & State Management",
        description: "Build multi-screen mobile apps",
        tasks: [
          { title: "Learn navigation patterns and routing", duration: "4 hours", type: "video" },
          { title: "Build app with multiple screens and navigation", duration: "6 hours", type: "project" },
          { title: "Implement state management (Redux/MobX)", duration: "5 hours", type: "practice" },
          { title: "Learn mobile API integration", duration: "2 hours", type: "reading" }
        ],
        unlocked: true
      };
    } else if (careerLower.includes('ui/ux') || careerLower.includes('designer')) {
      week1 = {
        week: 1,
        title: "UI/UX Design Fundamentals",
        description: "Learn design thinking and user research",
        tasks: [
          { title: "Set up Figma, Adobe XD, and design tools", duration: "2 hours", type: "setup" },
          { title: "Learn design principles and color theory", duration: "4 hours", type: "video" },
          { title: "Create a mood board and style guide", duration: "4 hours", type: "project" },
          { title: "Study user research methodologies", duration: "3 hours", type: "reading" }
        ],
        unlocked: true
      };
      week2 = {
        week: 2,
        title: "Wireframing & Prototyping",
        description: "Turn ideas into interactive prototypes",
        tasks: [
          { title: "Learn wireframing techniques", duration: "4 hours", type: "video" },
          { title: "Design a complete app interface in Figma", duration: "6 hours", type: "project" },
          { title: "Practice creating interactive prototypes", duration: "4 hours", type: "practice" },
          { title: "Study usability testing methods", duration: "2 hours", type: "reading" }
        ],
        unlocked: true
      };
    } else {
      // Default for other career paths
      week1 = {
      week: 1,
      title: "Foundation & Setup",
      description: "Get started with the fundamentals and set up your learning environment",
      tasks: [
        { title: "Set up development environment", duration: "2 hours", type: "setup" },
        { title: "Complete beginner course module 1-3", duration: "4 hours", type: "video" },
        { title: "Build your first simple project", duration: "3 hours", type: "project" },
        { title: "Join community forums and Discord", duration: "1 hour", type: "community" }
      ],
      unlocked: true
      };
      week2 = {
      week: 2,
      title: "Core Concepts & Practice",
      description: "Deep dive into core concepts with hands-on practice exercises",
      tasks: [
        { title: "Complete advanced tutorial series", duration: "5 hours", type: "video" },
        { title: "Practice problems (30+ challenges)", duration: "6 hours", type: "practice" },
        { title: "Read documentation and best practices", duration: "2 hours", type: "reading" },
        { title: "Build mini-project #2", duration: "4 hours", type: "project" }
      ],
      unlocked: true
      };
    }
    
    return [
      week1,
      week2,
    // Locked weeks (blurred preview)
    ...Array.from({ length: 14 }, (_, i) => ({
      week: i + 3,
      title: `Week ${i + 3} Content`,
      description: "Advanced topics and real-world projects",
      tasks: [],
      unlocked: false
    }))
  ];
  };

  const mockWeeks = generateCareerSpecificWeeks(careerPath);

  const getTaskIcon = (type) => {
    switch (type) {
      case 'video': return '📺';
      case 'practice': return '💻';
      case 'project': return '🚀';
      case 'reading': return '📖';
      case 'setup': return '⚙️';
      case 'community': return '👥';
      default: return '✓';
    }
  };

  return (
    <div className="space-y-6">
      <div className="text-center mb-8">
        <h2 className="text-3xl font-bold text-white mb-3 font-mono">YOUR LEARNING ROADMAP</h2>
        <p className="text-slate-400 font-mono">
          Get a preview of your personalized 16-week journey to becoming a <span className="text-emerald-400">{careerPath || 'professional'}</span>
        </p>
      </div>

      <div className="space-y-4">
        {mockWeeks.slice(0, 2).map((week, index) => (
          <motion.div
            key={week.week}
            initial={{ opacity: 0, x: -20 }}
            animate={{ opacity: 1, x: 0 }}
            transition={{ delay: index * 0.1 }}
            className="bg-[#0F131F] border border-white/10 rounded-2xl overflow-hidden"
          >
            {/* Week Header */}
            <button
              onClick={() => setExpandedWeek(expandedWeek === index ? -1 : index)}
              className="w-full p-6 flex items-center justify-between hover:bg-white/5 transition-colors"
            >
              <div className="flex items-center space-x-4">
                <div className="w-12 h-12 rounded-xl bg-gradient-to-br from-emerald-500/20 to-teal-500/20 flex items-center justify-center border border-emerald-500/30">
                  <span className="text-emerald-400 font-bold font-mono">{week.week}</span>
                </div>
                <div className="text-left">
                  <h3 className="text-lg font-bold text-white font-mono">{week.title}</h3>
                  <p className="text-sm text-slate-400 font-mono">{week.description}</p>
                </div>
              </div>
              <div className="flex items-center space-x-4">
                <span className="text-xs font-mono text-emerald-400 uppercase px-3 py-1 bg-emerald-500/10 rounded-full border border-emerald-500/30">
                  Free Preview
                </span>
                {expandedWeek === index ? (
                  <ChevronUp className="w-5 h-5 text-slate-400" />
                ) : (
                  <ChevronDown className="w-5 h-5 text-slate-400" />
                )}
              </div>
            </button>

            {/* Week Content */}
            <AnimatePresence>
              {expandedWeek === index && (
                <motion.div
                  initial={{ height: 0, opacity: 0 }}
                  animate={{ height: "auto", opacity: 1 }}
                  exit={{ height: 0, opacity: 0 }}
                  transition={{ duration: 0.3 }}
                  className="border-t border-white/10"
                >
                  <div className="p-6 space-y-3">
                    {week.tasks.map((task, taskIndex) => (
                      <div
                        key={taskIndex}
                        className="flex items-center space-x-3 p-4 bg-slate-800/50 rounded-xl border border-white/5 hover:border-emerald-500/30 transition-colors"
                      >
                        <span className="text-2xl">{getTaskIcon(task.type)}</span>
                        <div className="flex-1">
                          <p className="text-white font-medium font-mono text-sm">{task.title}</p>
                          <div className="flex items-center space-x-2 mt-1">
                            <Clock className="w-3 h-3 text-slate-500" />
                            <span className="text-xs text-slate-500 font-mono">{task.duration}</span>
                          </div>
                        </div>
                      </div>
                    ))}
                  </div>
                </motion.div>
              )}
            </AnimatePresence>
          </motion.div>
        ))}

        {/* Locked Weeks Preview */}
        <div className="relative">
          {/* Blurred Content */}
          <div className="filter blur-sm pointer-events-none">
            {mockWeeks.slice(2, 5).map((week) => (
              <div
                key={week.week}
                className="bg-[#0F131F] border border-white/10 rounded-2xl p-6 mb-4"
              >
                <div className="flex items-center space-x-4">
                  <div className="w-12 h-12 rounded-xl bg-slate-700/50 flex items-center justify-center">
                    <span className="text-slate-500 font-bold font-mono">{week.week}</span>
                  </div>
                  <div>
                    <div className="h-4 bg-slate-700/50 rounded w-48 mb-2"></div>
                    <div className="h-3 bg-slate-700/30 rounded w-64"></div>
                  </div>
                </div>
              </div>
            ))}
          </div>

          {/* Overlay */}
          <div className="absolute inset-0 bg-gradient-to-b from-transparent via-[#0B0F19]/80 to-[#0B0F19] flex items-center justify-center">
            <motion.div
              initial={{ opacity: 0, y: 20 }}
              animate={{ opacity: 1, y: 0 }}
              className="text-center z-10 p-8"
            >
              <div className="w-20 h-20 rounded-2xl bg-gradient-to-br from-yellow-500/20 to-orange-500/20 flex items-center justify-center mx-auto mb-6 border border-yellow-500/30">
                <Lock className="w-10 h-10 text-yellow-400" />
              </div>
              <h3 className="text-2xl font-bold text-white mb-3 font-mono">WEEKS 3-16 LOCKED</h3>
              <p className="text-slate-400 mb-6 max-w-md font-mono">
                Unlock the complete roadmap with detailed tasks, resources, milestones, and portfolio projects
              </p>
              <div className="flex flex-col sm:flex-row gap-3 justify-center">
                <button
                  onClick={onUnlock}
                  className="bg-gradient-to-r from-emerald-500 to-teal-500 text-white py-4 px-8 rounded-xl font-semibold hover:scale-105 transition-all flex items-center justify-center space-x-2 font-mono"
                >
                  <Target className="w-5 h-5" />
                  <span>UNLOCK FULL ROADMAP</span>
                </button>
              </div>

              {/* Stats Preview */}
              <div className="mt-8 grid grid-cols-3 gap-4 max-w-lg mx-auto">
                <div className="bg-[#0F131F]/80 border border-white/10 rounded-xl p-4">
                  <div className="text-2xl font-bold text-emerald-400 font-mono">14</div>
                  <div className="text-xs text-slate-400 font-mono">More Weeks</div>
                </div>
                <div className="bg-[#0F131F]/80 border border-white/10 rounded-xl p-4">
                  <div className="text-2xl font-bold text-emerald-400 font-mono">80+</div>
                  <div className="text-xs text-slate-400 font-mono">Tasks</div>
                </div>
                <div className="bg-[#0F131F]/80 border border-white/10 rounded-xl p-4">
                  <div className="text-2xl font-bold text-emerald-400 font-mono">5</div>
                  <div className="text-xs text-slate-400 font-mono">Projects</div>
                </div>
              </div>
            </motion.div>
          </div>
        </div>
      </div>
    </div>
  );
};

export default RoadmapTeaserSection;

